/**
 * AdExamplesGallery — landing-page image showcase of REAL Lumora-generated ads.
 *
 * All 8 assets under /demo-ads/ad-{1..8}.jpg were generated end-to-end by
 * Lumora's Nano Banana image engine (see backend/scripts/seed_demo_ads.py).
 * To regenerate with new prompts, edit that script and run:
 *     cd /app/backend && python -m scripts.seed_demo_ads --force
 */
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Filter, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const ALL_TAG = "All";
const CATEGORIES = [ALL_TAG, "Beauty", "Fashion", "Tech", "Homewares", "Supplements"];

const EXAMPLES = [
  { id: "ad-1", category: "Beauty",      style: "Serum shot",     hook: "Found my new holy-grail serum.",       stat: "4.2× ROAS · Byron Bay skincare",       src: "/demo-ads/ad-1.jpg", accent: "from-pink-500/30 to-rose-700/30" },
  { id: "ad-2", category: "Beauty",      style: "Glow-up",        hook: "Wait — this glow is from one bottle?", stat: "1.8M impressions in 14 days",          src: "/demo-ads/ad-2.jpg", accent: "from-fuchsia-500/30 to-pink-700/30" },
  { id: "ad-3", category: "Fashion",     style: "Street-style",   hook: "Outfit of the day, ranked.",           stat: "32% CTR uplift vs static images",      src: "/demo-ads/ad-3.jpg", accent: "from-violet-500/30 to-indigo-700/30" },
  { id: "ad-4", category: "Fashion",     style: "Sunrise run",    hook: "The only activewear I run in now.",    stat: "2.1× CTR vs studio shoot",             src: "/demo-ads/ad-4.jpg", accent: "from-violet-500/30 to-fuchsia-700/30" },
  { id: "ad-5", category: "Tech",        style: "Product flat-lay", hook: "Setup took 30 seconds.",             stat: "$0.42 CPM on Meta",                    src: "/demo-ads/ad-5.jpg", accent: "from-cyan-500/30 to-blue-700/30" },
  { id: "ad-6", category: "Tech",        style: "POV outdoor",    hook: "Why I ditched my old wearable.",       stat: "5.7× ROAS · DTC tech brand",           src: "/demo-ads/ad-6.jpg", accent: "from-sky-500/30 to-cyan-700/30" },
  { id: "ad-7", category: "Homewares",   style: "Warm shelf",     hook: "The candle my whole flat asks about.", stat: "12% conversion from cold traffic",     src: "/demo-ads/ad-7.jpg", accent: "from-violet-500/30 to-indigo-700/30" },
  { id: "ad-8", category: "Supplements", style: "Morning ritual", hook: "Day 7 — no more 3pm crash.",           stat: "3.4× ROAS · supplements DTC",          src: "/demo-ads/ad-8.jpg", accent: "from-fuchsia-500/30 to-violet-700/30" },
];

function ExampleCard({ ex, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ delay: i * 0.05, duration: 0.4 }}
      className="glass rounded-2xl overflow-hidden ring-1 ring-white/5 card-hover group"
      data-testid={`example-ad-${ex.id}`}
    >
      <div className={`relative aspect-[9/16] bg-gradient-to-br ${ex.accent}`}>
        <img
          src={ex.src}
          alt={`${ex.category} ad — ${ex.hook}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`example-ad-img-${ex.id}`}
        />

        {/* Subtle dark gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30 pointer-events-none" />

        {/* Mock UI chrome */}
        <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-[9px] font-bold uppercase tracking-wider ring-1 ring-white/20">
          {ex.style}
        </div>
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-violet-500/25 backdrop-blur-sm text-violet-100 text-[9px] font-bold uppercase tracking-wider ring-1 ring-violet-300/30">
          AI · 9:16
        </div>

        {/* Hook quote */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
          <p className="text-white text-sm font-bold leading-tight">&ldquo;{ex.hook}&rdquo;</p>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-violet-300 text-[10px] font-bold uppercase tracking-wider">{ex.category}</span>
          <span className="text-white/30 text-[10px]">Generated in &lt;10s</span>
        </div>
        <p className="text-white/65 text-xs leading-relaxed">{ex.stat}</p>
      </div>
    </motion.div>
  );
}

export function AdExamplesGallery() {
  const [active, setActive] = useState(ALL_TAG);

  const filtered = useMemo(() => {
    if (active === ALL_TAG) return EXAMPLES;
    return EXAMPLES.filter((e) => e.category === active);
  }, [active]);

  return (
    <section
      id="examples"
      className="py-10 md:py-14 relative"
      data-testid="ad-examples-gallery"
      aria-label="Sample ads generated by Lumora AI"
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-violet-300 mb-5">
            <Sparkles className="w-4 h-4" /> Every image below made by Lumora AI · Zero filming
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3">
            <span className="gradient-text-warm">See what Lumora generates for brands like yours</span>
          </h2>
          <p className="text-white/55 max-w-2xl mx-auto text-sm md:text-base">
            Every ad below was generated from a Shopify product brief in under 10 seconds. No filming. No creators. No agency. Filter by your category to see the format that fits.
          </p>
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8" data-testid="ad-examples-filters">
          <Filter className="w-3.5 h-3.5 text-white/40 mr-1" />
          {CATEGORIES.map((cat) => {
            const isActive = active === cat;
            return (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`example-filter-${cat.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-lg shadow-violet-500/30"
                    : "bg-white/5 text-white/55 hover:bg-white/10 hover:text-white border border-white/10"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3 md:gap-4">
          {filtered.map((ex, i) => (
            <ExampleCard key={ex.id} ex={ex} i={i} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-white/40 mt-8" data-testid="examples-empty">
            More examples for this category coming soon.
          </p>
        )}

        {/* CTA */}
        <div className="text-center mt-12">
          <Button asChild className="rounded-full px-8 py-6 text-base bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-violet-500/25" data-testid="ad-examples-cta">
            <Link to="/register">Generate your first ad free <ArrowRight className="ml-2 w-4 h-4" /></Link>
          </Button>
          <p className="text-white/40 text-xs mt-3">No credit card required · 30 free credits</p>
        </div>
      </div>
    </section>
  );
}
